import { create } from 'zustand';
import type { MyPartProjects, User } from '../interfaces/user';
import { ModalType } from '../interfaces/modal-types.enum';
import { environment } from '../config/environment';
import { manuallyAddScript } from '../shared/utils/custom-hotjar-script';
import { useGlobalStore } from './global-store';
import { useModalStore } from './modal-store';

// Port of src/app/services/user-state.service.ts.
//
// NOTE(port): the Angular constructor auto-started the sign-up popup timer when
// no user was logged in. In React, call initUserStateTimers() once at app
// startup (e.g. in App's mount effect) to replicate this.

// Private (non-signal) service fields → module-level mutable state.
let timerHandle: ReturnType<typeof setInterval> | undefined;
let totalSongsPlayed = 0;

interface UserState {
  isUserWhiteLabel: boolean;
  user: User | null;
  // Angular: private _utmTags with get/set accessors (setter trimmed). Use
  // setUtmTags() to write; read the field directly.
  utmTags: string | undefined;

  setUtmTags: (value: string | undefined) => void;
  setUser: (user: User | null) => void;
  checkUserHasExceededAllowedSongs: () => void;
  setProjectIds: () => void;
  initPreferences: (user: User | null) => void;
  checkUtmTags: (fromSignup?: boolean) => boolean;
}

/**
 * Returns true if the current UTM source is 'overwolf'
 * (Angular: `get isOverwolfSource` on UserStateService.)
 */
export function isOverwolfSource(): boolean {
  const utmTags = useUserStore.getState().utmTags;
  if (!utmTags) return false;
  const params = new URLSearchParams(utmTags);
  return params.get('utm_source')?.toLowerCase() === 'overwolf';
}

// Show sign up popup after user is not subscribed and in the app for 2 minutes.
// Angular used `interval(2min).pipe(takeWhile(() => !user))` — the takeWhile
// stops the stream as soon as a user exists; replicated below.
function startTimer(): void {
  const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds

  stopTimer();
  timerHandle = setInterval(() => {
    if (useUserStore.getState().user) {
      stopTimer(); // takeWhile(() => !user) — stop once a user exists
      return;
    }
    showSignUpPopup();
  }, twoMinutes);
}

function stopTimer(): void {
  if (timerHandle) {
    clearInterval(timerHandle);
    timerHandle = undefined;
  }
}

function showSignUpPopup(): void {
  if (!useUserStore.getState().user) {
    useModalStore.getState().open(ModalType.LOGIN_REGISTER, {
      inputs: {
        loginPopupTitle: 'Sign Up for FREE for unlimited access to Songhunt',
        hideCloseButton: true
      }
    });
  }
}

function extractProjectIds(projects: MyPartProjects[]): string {
  return projects.map((p) => p.id).join(',');
}

/**
 * Replicates the Angular constructor: start the sign-up popup timer when there
 * is no logged-in user. Call once from App on mount.
 */
export function initUserStateTimers(): void {
  if (!useUserStore.getState().user) {
    startTimer();
  }
}

export const useUserStore = create<UserState>()((set, get) => ({
  isUserWhiteLabel: false,
  user: null,
  utmTags: undefined,

  setUtmTags: (value) => set({ utmTags: value?.trim() }), // Optional: Trim whitespace

  setUser: (user) => {
    // if (user?.statusId === 1) {
    //   user.isSubscribed = true;
    // }

    get().initPreferences(user);

    if (!get().user && user?.email !== environment.defaultUserEmail) {
      manuallyAddScript(user!.email);
    }

    const isWhiteLabel = user?.accountTypeId === 4;
    set({ user, isUserWhiteLabel: isWhiteLabel });

    get().setProjectIds();
    if (user) {
      stopTimer();
    } else {
      startTimer();
    }
  },

  checkUserHasExceededAllowedSongs: () => {
    totalSongsPlayed++;
    if (totalSongsPlayed > 5) {
      showSignUpPopup();
      totalSongsPlayed = 1;
    }
  },

  setProjectIds: () => {
    const isWhiteLabel = get().isUserWhiteLabel;
    const user = get().user;
    const globalState = useGlobalStore.getState();
    globalState.setGlobalField('projectIdsToUse',
      isWhiteLabel && user?.projects
        ? extractProjectIds(user.projects)
        : globalState.publicProjectIds.join(','));
  },

  initPreferences: (user) => {
    const globalState = useGlobalStore.getState();
    if (user) {
      const userTheme: 'dark' | 'light' = user.preferences['theme'] as 'dark' | 'light';
      const userDiscovery = user.preferences['discovery'];
      // TEMP(SimCSE-only): kept for parity with Angular source (unused there too while the check below is disabled).
      // const userSemanticsModel = user.preferences['semanticsModel'] as 'SimCSE' | 'Roberta';

      const localTheme = localStorage.getItem('theme');
      const localDiscovery = localStorage.getItem('discovery');
      // const localSemanticsModel = localStorage.getItem('semanticsModel');

      // TEMP(SimCSE-only): semanticsModel excluded from the mismatch check so the prefs modal does not
      // pop up for the now-hidden Multilingual Mode setting. REVERT when the backend multilingual model
      // is fixed — user will confirm timing.
      if (userTheme !== localTheme || userDiscovery !== localDiscovery /* || userSemanticsModel !== localSemanticsModel */) {
        useModalStore.getState().open(ModalType.DIFFERENT_USER_PREFERENCES);
      }
      // Always update global state with local values
      globalState.setGlobalField('appTheme', (localTheme as 'dark' | 'light') || 'light');
      globalState.setGlobalField('discoveryMode', (localDiscovery || 'false') === 'true');
      // TEMP(SimCSE-only): force SimCSE regardless of stored preference until the backend multilingual
      // model is fixed. REVERT to the line below when the backend is ready — user will confirm timing.
      globalState.setGlobalField('semanticsModel', 'SimCSE');
      // globalState.setGlobalField('semanticsModel', (localSemanticsModel as 'SimCSE' | 'Roberta') || 'SimCSE');
    } else {
      const discovery = localStorage.getItem('discovery');
      if (discovery) {
        globalState.setGlobalField('discoveryMode', discovery === 'true');
      } else {
        localStorage.setItem('discovery', 'false');
        globalState.setGlobalField('discoveryMode', false);
      }

      // TEMP(SimCSE-only): force SimCSE until the backend multilingual model is fixed. REVERT to the
      // commented block below when the backend is ready — user will confirm timing.
      localStorage.setItem('semanticsModel', 'SimCSE');
      globalState.setGlobalField('semanticsModel', 'SimCSE');
      // const semanticsModel = localStorage.getItem('semanticsModel');
      // if (semanticsModel) {
      //   globalState.setGlobalField('semanticsModel', semanticsModel as 'SimCSE' | 'Roberta');
      // } else {
      //   localStorage.setItem('semanticsModel', 'SimCSE');
      //   globalState.setGlobalField('semanticsModel', 'SimCSE');
      // }
    }
  },

  checkUtmTags: (fromSignup = false) => {
    try {
      // Parse the current URL
      const url = new URL(window.location.href);

      // Define the base path
      const basePath = '/songhunt';

      // Get the path after '/songhunt'
      let pathAfterBase = url.pathname.substring(basePath.length);

      // Normalize pathAfterBase by ensuring it starts with '/'
      pathAfterBase = pathAfterBase.startsWith('/') ? pathAfterBase : `/${pathAfterBase}`;

      if (!fromSignup) {
        // Define allowed paths after '/songhunt' (include both with and without trailing slash)
        const allowedPaths = new Set(['', '/', '/signup', '/signup/', '/register', '/register/', '/affiliate-program', '/affiliate-program/', '/similar', '/artist']);

        // If there's a path and it's not one of the allowed ones, return false
        if (pathAfterBase && !allowedPaths.has(pathAfterBase)) {
          return false; // Indicates 'showResults' state
        }
      }

      // Get query parameters
      const queryParams = url.searchParams;

      // Define known UTM tags
      const utmTags = new Set([
        'via',
        'ref',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'utm_social',
      ]);

      // Flag to determine if only UTM tags are present
      let onlyUtmTags = true;

      // Object to hold extracted UTM parameters
      const extractedUtmParams: { [key: string]: string } = {};

      // Iterate over all query parameters
      queryParams.forEach((value, key) => {
        if (utmTags.has(key)) {
          // Save allowed UTM parameter
          extractedUtmParams[key] = value;
        } else {
          // Found a parameter that's not allowed
          onlyUtmTags = false;
        }
      });

      // Construct the UTM string
      let utmString: string | undefined = undefined;
      if (Object.keys(extractedUtmParams).length > 0) {
        // Encode each key and value to ensure URL safety
        const utmParamPairs = Object.entries(extractedUtmParams)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);

        // Join the pairs with '&' to form a query-like string
        utmString = utmParamPairs.join('&');
      }

      if (utmString) {
        // Save the UTM string to the signal
        get().setUtmTags(utmString);
      }

      return onlyUtmTags; // true for 'showSearch', false for 'showResults'
    } catch (error) {
      console.error('Error in checkUtmTags:', error);
      // In case of error, you might choose to return false or handle it differently
      get().setUtmTags(undefined);
      return false;
    }
  },
}));
